import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from 'components/auth';
import { WorkspaceType, fetchWorkspace } from '../api';

interface WorkspaceContextType {
  workspace: WorkspaceType | null;
  setWorkspace: (workspace: WorkspaceType) => void;
  clearWorkspace: () => void;
  isLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspace: null,
  setWorkspace: () => {},
  clearWorkspace: () => {},
  isLoading: false,
});

export const useWorkspace = () => useContext(WorkspaceContext);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [workspace, setWorkspaceState] = useState<WorkspaceType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (clearedRef.current) return;

    const storedId = localStorage.getItem('activeWorkspaceId');
    const activeId = storedId ? Number(storedId) : null;

    if (activeId) {
      fetchWorkspace(activeId)
        .then((res) => {
          setWorkspaceState(res.data);
          localStorage.setItem('activeWorkspaceId', String(activeId));
        })
        .catch(() => {
          localStorage.removeItem('activeWorkspaceId');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [user?.activeWorkspaceId]);

  const setWorkspace = useCallback((ws: WorkspaceType) => {
    clearedRef.current = false;
    setWorkspaceState(ws);
    localStorage.setItem('activeWorkspaceId', String(ws.id));
    if (window.location.pathname !== '/') window.location.href = '/';
  }, []);

  const clearWorkspace = useCallback(() => {
    clearedRef.current = true;
    setWorkspaceState(null);
    localStorage.removeItem('activeWorkspaceId');
    if (window.location.pathname !== '/') window.location.href = '/';
  }, []);

  return (
    <WorkspaceContext.Provider value={{ workspace, setWorkspace, clearWorkspace, isLoading }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
